import { useEffect, useState } from "react";
import { Tabs, message } from "antd";
import type { TabsProps } from "antd";
import { Colors } from "../../constants/Colors";
import { data } from "../../data/data";
import ClubAppUserList from "./components/ClubAppUserList";
import { useGetAllUsersQuery } from "../../graphql/operations/get-all-users";
import Spinner from "../../components/layout/spinner/Spinner";
import MktUserList from "./components/MktUserList";
import SalesUserList from "./components/SalesUserList";
import TrialUserList from "./components/TrialUserList";
import DemoAppUserList from "./components/DemoAppUserList";
import ZippyUserList from "./components/ZippyUserList";
import { renderAntDMessageConfig } from "../../utils/renderAntDMessageConfig";
import { __isPermissionPage__ } from "../../graphql/policies";

const { services } = data;

const ProgramSetting = () => {
  const [clubAppUsers, setClubAppUsers] = useState<any>([]);
  const [mktUsers, setMktUsers] = useState<any>([]);
  const [trialUsers, setTrialUsers] = useState<any>([]);
  const [salesUsers, setSalesUsers] = useState<any>([]);
  const [demoAppUsers, setDemoAppUsers] = useState<any>([]);
  const [zippyUsers, setZippyUsers] = useState<any>([]);
  const { data, loading, error } = useGetAllUsersQuery();
  __isPermissionPage__(true);

  const renderTabChildren = (index: number) => {
    switch (index) {
      case 0:
        return <ClubAppUserList users={clubAppUsers} />;
      case 6:
        return <MktUserList users={mktUsers} />;
      case 5:
        return <TrialUserList users={trialUsers} />;
      case 4:
        return <DemoAppUserList users={demoAppUsers} />;
      case 3:
        return <SalesUserList users={salesUsers} />;
      case 8:
        return <ZippyUserList users={zippyUsers} />;
      default:
        break;
    }
  };

  const items: TabsProps["items"] = services.map((item, i) => ({
    key: item.id.toString(),
    label: item.service,
    children: renderTabChildren(i),
    disabled: i !== 0 && i !== 4 && i !== 5 && i !== 3 && i !== 6 && i !== 8,
  }));

  useEffect(() => {
    if (data) {
      const usersOfClubApp =
        data.wf_adminFirst_afUser?.edges &&
        data.wf_adminFirst_afUser?.edges.map((user) => ({
          id: user.node.id,
          name: user.node.userName,
          market:
            user.node.afUserMarkets &&
            user.node.afUserMarkets.map((market) => market?.market),
          notification:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Notification",
            )
              ? true
              : false,
          faceCall:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Face Call",
            )
              ? true
              : false,
          videoManagement:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Kids World",
            )
              ? true
              : false,
          warranty:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Warranty",
            )
              ? true
              : false,
          announcementBar:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Announcement Bar",
            )
              ? true
              : false,
          serviceBanner:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Service Banner",
            )
              ? true
              : false,
          eventBanner:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Event Banner",
            )
              ? true
              : false,
          goGoLive:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Go, Go Live!",
            )
              ? true
              : false,
          importantNotice:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Important Notice",
            )
              ? true
              : false,
          waitingForReview:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Waiting for Review",
            )
              ? true
              : false,
          graduationReviewed:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Graduation Reviewed",
            )
              ? true
              : false,
          kidsInfo:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Kids Info",
            )
              ? true
              : false,
          releaseVersion:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Release Version",
            )
              ? true
              : false,
          buyUpQuery:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Buy Up Query",
            )
              ? true
              : false,
          powerBiTest:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Power BI Test",
            )
              ? true
              : false,
          entryPackageQuery:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Entry Package Query",
            )
              ? true
              : false,
        }));
      const usersOfMkt =
        data.wf_adminFirst_afUser?.edges &&
        data.wf_adminFirst_afUser?.edges.map((user) => ({
          id: user.node.id,
          name: user.node.userName,
          market:
            user.node.afUserMarkets &&
            user.node.afUserMarkets.map((market) => market?.market),
          vendorReports:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Vendor Reports",
            )
              ? true
              : false,
          getWinner:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Get Winner",
            )
              ? true
              : false,
          mergeLeads:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Merge Leads",
            )
              ? true
              : false,
          updateSourceCode:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Update Source Code",
            )
              ? true
              : false,
          ealmBonus:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "EALM Bonus",
            )
              ? true
              : false,
          ealmBonusIncentive:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "EALM Bonus Incentive",
            )
              ? true
              : false,
          ealmVendorPerformance:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "EALM Vendor Performance",
            )
              ? true
              : false,
          internetSourceCost:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Internet Source Cost",
            )
              ? true
              : false,
          adExclusionList:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Ad Exclusion List",
            )
              ? true
              : false,
          internetLeadsConversion:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Internet Leads Conversion",
            )
              ? true
              : false,
        }));
      const usersOfTrial =
        data.wf_adminFirst_afUser?.edges &&
        data.wf_adminFirst_afUser?.edges.map((user) => ({
          id: user.node.id,
          name: user.node.userName,
          market:
            user.node.afUserMarkets &&
            user.node.afUserMarkets.map((market) => market?.market),
          leadsDetailDownload:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Leads Detail Download",
            )
              ? true
              : false,
          campaignId:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Campaign ID",
            )
              ? true
              : false,
          localEvent:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Local Event",
            )
              ? true
              : false,
          caringCTA:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Caring CTA",
            )
              ? true
              : false,
          journeyCard:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Journey Card",
            )
              ? true
              : false,
          trialContent:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Trial Content",
            )
              ? true
              : false,
          trialContentUpload:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Trial Content Upload",
            )
              ? true
              : false,
          videoManagement:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Video Management",
            )
              ? true
              : false,
          pushNotification:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Push Notification",
            )
              ? true
              : false,
          trialAnnouncementBar:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Trial Announcement Bar",
            )
              ? true
              : false,
        }));
      const usersOfSales =
        data.wf_adminFirst_afUser?.edges &&
        data.wf_adminFirst_afUser?.edges.map((user) => ({
          id: user.node.id,
          name: user.node.userName,
          market:
            user.node.afUserMarkets &&
            user.node.afUserMarkets.map((market) => market?.market),
          releaseVersion:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Sales Release Version",
            )
              ? true
              : false,
          pushNotification:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Sales Notification",
            )
              ? true
              : false,
          leadManagement:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Lead Management",
            )
              ? true
              : false,
          businessManagement:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Business Management",
            )
              ? true
              : false,
          systemManagement:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "System Management",
            )
              ? true
              : false,
          memberInfo:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Member Info",
            )
              ? true
              : false,
          salesContestReport:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Sales Contest Report",
            )
              ? true
              : false,
        }));
      const usersOfDemoApp =
        data.wf_adminFirst_afUser?.edges &&
        data.wf_adminFirst_afUser?.edges.map((user) => ({
          id: user.node.id,
          name: user.node.userName,
          market:
            user.node.afUserMarkets &&
            user.node.afUserMarkets.map((market) => market?.market),
          videoManagement:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Video management",
            )
              ? true
              : false,
        }));
      const usersOfZippy =
        data.wf_adminFirst_afUser?.edges &&
        data.wf_adminFirst_afUser?.edges.map((user) => ({
          id: user.node.id,
          name: user.node.userName,
          market:
            user.node.afUserMarkets &&
            user.node.afUserMarkets.map((market) => market?.market),
          storyTime:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Story Time",
            )
              ? true
              : false,
          dailyCheckIn:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Daily Check-in",
            )
              ? true
              : false,
          channel:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) => program?.afProgram?.programName === "Channel",
            )
              ? true
              : false,
          faceCall:
            user.node.afUserPrograms &&
            user.node.afUserPrograms.find(
              (program) =>
                program?.afProgram?.programName === "Zippy Face Call",
            )
              ? true
              : false,
        }));
      setClubAppUsers(usersOfClubApp);
      setMktUsers(usersOfMkt);
      setTrialUsers(usersOfTrial);
      setSalesUsers(usersOfSales);
      setDemoAppUsers(usersOfDemoApp);
      setZippyUsers(usersOfZippy);
    }
  }, [data]);

  if (error) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator.",
      ),
    );
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <div
      className="permission-container"
      style={{ backgroundColor: Colors.lightWhite }}
    >
      <div
        className="permission-inner-container"
        style={{ backgroundColor: Colors.white }}
      >
        <div className="permission-header-container">
          <p>Program permission settings</p>
        </div>
        <div className="permission-content-container">
          <Tabs items={items} />
        </div>
      </div>
    </div>
  );
};

export default ProgramSetting;
